import React from 'react';
import type { Editor } from '@tiptap/react';

interface ToolbarProps {
  editor: Editor | null;
  onImageUpload: () => void;
  onLinkAdd: () => void;
}

interface ToolbarButton {
  label: string;
  title: string;
  action: () => void;
  isActive?: () => boolean;
}

const btnBase =
  'px-2 py-1 text-sm rounded hover:bg-gray-200 transition-colors font-medium';
const btnActive = 'bg-gray-200 text-blue-600';

export const Toolbar: React.FC<ToolbarProps> = ({ editor, onImageUpload, onLinkAdd }) => {
  if (!editor) return null;

  const buttons: ToolbarButton[] = [
    {
      label: 'B',
      title: 'Bold (Ctrl+B)',
      action: () => editor.chain().focus().toggleBold().run(),
      isActive: () => editor.isActive('bold'),
    },
    {
      label: 'I',
      title: 'Italic (Ctrl+I)',
      action: () => editor.chain().focus().toggleItalic().run(),
      isActive: () => editor.isActive('italic'),
    },
    {
      label: 'U',
      title: 'Underline (Ctrl+U)',
      action: () => editor.chain().focus().toggleUnderline().run(),
      isActive: () => editor.isActive('underline'),
    },
    {
      label: 'S̶',
      title: 'Strikethrough',
      action: () => editor.chain().focus().toggleStrike().run(),
      isActive: () => editor.isActive('strike'),
    },
    {
      label: '`',
      title: 'Inline Code',
      action: () => editor.chain().focus().toggleCode().run(),
      isActive: () => editor.isActive('code'),
    },
  ];

  const headingLevels = [1, 2, 3, 4, 5, 6] as const;

  const alignButtons = [
    { label: '⬛', title: 'Align Left', align: 'left' as const },
    { label: '▣', title: 'Align Center', align: 'center' as const },
    { label: '⬜', title: 'Align Right', align: 'right' as const },
  ];

  return (
    <div className="flex items-center gap-1 px-3 py-2 bg-white border-b border-gray-200 flex-wrap">
      {/* Text formatting */}
      {buttons.map((btn) => (
        <button
          key={btn.label}
          title={btn.title}
          onClick={btn.action}
          className={`${btnBase} ${btn.isActive?.() ? btnActive : 'text-gray-700'}`}
        >
          {btn.label}
        </button>
      ))}

      <div className="w-px h-5 bg-gray-300 mx-1" />

      {/* Headings */}
      <select
        title="Heading Level"
        className="text-sm border border-gray-300 rounded px-1 py-0.5 bg-white text-gray-700 hover:border-gray-400"
        value={
          headingLevels.find((l) => editor.isActive('heading', { level: l })) || ''
        }
        onChange={(e) => {
          const val = e.target.value;
          if (val === '') {
            editor.chain().focus().setParagraph().run();
          } else {
            editor
              .chain()
              .focus()
              .toggleHeading({ level: Number(val) as 1 | 2 | 3 | 4 | 5 | 6 })
              .run();
          }
        }}
      >
        <option value="">Paragraph</option>
        {headingLevels.map((l) => (
          <option key={l} value={l}>
            Heading {l}
          </option>
        ))}
      </select>

      <div className="w-px h-5 bg-gray-300 mx-1" />

      {/* Lists */}
      <button
        title="Bullet List"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={`${btnBase} ${editor.isActive('bulletList') ? btnActive : 'text-gray-700'}`}
      >
        • List
      </button>
      <button
        title="Ordered List"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={`${btnBase} ${editor.isActive('orderedList') ? btnActive : 'text-gray-700'}`}
      >
        1. List
      </button>

      <div className="w-px h-5 bg-gray-300 mx-1" />

      {/* Alignment */}
      {alignButtons.map((btn) => (
        <button
          key={btn.align}
          title={btn.title}
          onClick={() => editor.chain().focus().setTextAlign(btn.align).run()}
          className={`${btnBase} ${editor.isActive({ textAlign: btn.align }) ? btnActive : 'text-gray-700'}`}
        >
          {btn.label}
        </button>
      ))}

      <div className="w-px h-5 bg-gray-300 mx-1" />

      {/* Block elements */}
      <button
        title="Blockquote"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={`${btnBase} ${editor.isActive('blockquote') ? btnActive : 'text-gray-700'}`}
      >
        ❝
      </button>
      <button
        title="Code Block"
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        className={`${btnBase} ${editor.isActive('codeBlock') ? btnActive : 'text-gray-700'}`}
      >
        {'</>'}
      </button>
      <button
        title="Horizontal Rule"
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        className={`${btnBase} text-gray-700`}
      >
        —
      </button>

      <div className="w-px h-5 bg-gray-300 mx-1" />

      {/* Table */}
      <button
        title="Insert Table"
        onClick={() =>
          editor
            .chain()
            .focus()
            .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
            .run()
        }
        className={`${btnBase} text-gray-700`}
      >
        Table
      </button>

      <div className="w-px h-5 bg-gray-300 mx-1" />

      {/* Link */}
      <button
        title="Add Link"
        onClick={onLinkAdd}
        className={`${btnBase} ${editor.isActive('link') ? btnActive : 'text-gray-700'}`}
      >
        🔗
      </button>

      {/* Image */}
      <button
        title="Upload Image"
        onClick={onImageUpload}
        className={`${btnBase} text-gray-700`}
      >
        🖼️
      </button>

      <div className="w-px h-5 bg-gray-300 mx-1" />

      {/* Admonition */}
      <select
        title="Insert Admonition"
        className="text-sm border border-gray-300 rounded px-1 py-0.5 bg-white text-gray-700 hover:border-gray-400"
        value=""
        onChange={(e) => {
          const type = e.target.value;
          if (type) {
            editor
              .chain()
              .focus()
              .insertContent({
                type: 'admonition',
                attrs: { type, title: '' },
                content: [{ type: 'paragraph' }],
              })
              .run();
          }
          e.target.value = '';
        }}
      >
        <option value="">+ Admonition</option>
        {[
          'note', 'abstract', 'info', 'tip', 'success', 'question',
          'warning', 'failure', 'danger', 'bug', 'example', 'quote',
        ].map((t) => (
          <option key={t} value={t}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </option>
        ))}
      </select>

      {/* Mermaid */}
      <button
        title="Insert Mermaid Diagram"
        onClick={() =>
          editor
            .chain()
            .focus()
            .insertContent({
              type: 'mermaidBlock',
              attrs: { code: 'graph LR\n    A --> B' },
            })
            .run()
        }
        className={`${btnBase} text-gray-700`}
      >
        ◻ Diagram
      </button>

      <div className="ml-auto flex gap-1">
        {/* Undo/Redo */}
        <button
          title="Undo (Ctrl+Z)"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          className={`${btnBase} text-gray-700 disabled:opacity-40`}
        >
          ↩
        </button>
        <button
          title="Redo (Ctrl+Y)"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          className={`${btnBase} text-gray-700 disabled:opacity-40`}
        >
          ↪
        </button>
      </div>
    </div>
  );
};
